<template>
  <q-card>
    <q-form @submit="submit">
      <q-card-section>
        <div class="text-h6 text-center">New Bank Account</div>
      </q-card-section>

      <q-card-section>
        <div class="row q-pb-md">
          <div class="col-xs-12 col-sm-4 col-md-4 q-px-xs">
            <q-toggle :label="'Default Account'" color="primary" v-model="isDefaultBankAcc" />
          </div>
          <div class="col-xs-12 col-sm-4 col-md-4 q-px-xs">
            <q-toggle :label="'Joint Account'" color="primary" v-model="isJointAccount" @input="fetchCustomerAccounts" />
          </div>
          <div class="col-xs-12 col-sm-4 col-md-4 q-px-xs">
            <q-toggle :label="'3rd Party Individual'" color="primary" v-model="is3rdPartyAccount" @input="fetchCustomerAccounts" />
          </div>
        </div>

        <div class="row q-pb-md">
          <div class="col-xs-12 col-sm-12 col-md-12 q-px-xs q-pb-md">
            <q-input
              outlined
              stack-label
              class="q-pb-none"
              v-model="holderName"
              label="Account Holder Name"
              lazy-rules
              :rules="[ val => val !== '' && val !== null ]"
            />
          </div>

          <div class="col-xs-12 col-sm-6 col-md-6 q-px-xs q-pb-md">
            <q-select
              outlined
              stack-label
              class="q-pb-none"
              color="primary"
              label="Validation Id Type"
              v-model="validationIdType"
              :options="idTypeList"
              lazy-rules
              :rules="[ val => val !== '' && val !== null ]"
            />
          </div>
          <div class="col-xs-12 col-sm-6 col-md-6 q-px-xs q-pb-md">
            <q-input
              outlined
              stack-label
              class="q-pb-none"
              v-model="validationId"
              v-on:keydown="inputNoSpecialChar"
              label="Validation Id"
              lazy-rules
              :rules="[ val => val !== '' && val !== null ]"
            />
          </div>

          <div class="col-xs-12 col-sm-12 col-md-12 q-px-xs q-pb-md">
            <q-select
              outlined
              stack-label
              class="q-pb-none"
              color="primary"
              label="Bank Name"
              ref="bankList"
              v-model="bank"
              :options="bankList"
              lazy-rules
              :rules="[ val => val !== '' && val !== null ]"
            />
          </div>

          <div class="col-xs-12 col-sm-6 col-md-6 q-px-xs q-pb-md">
            <q-input
              outlined
              stack-label
              class="q-pb-none"
              v-model.number="bankAccNumber"
              type="number"
              v-on:keydown="inputNoSpecialChar"
              label="Bank Account No."
              lazy-rules
              :rules="[ val => val !== '' && val !== null ]"
            />
          </div>
          <div class="col-xs-12 col-sm-6 col-md-6 q-px-xs q-pb-md">
            <q-select
              outlined
              stack-label
              class="q-pb-none"
              color="primary"
              label="Bank Currency"
              v-model="bankCurrency"
              @input="fetchCustomerAccounts"
              :options="bankCurrencyList"
              lazy-rules
              :rules="[ val => val !== '' && val !== null ]"
              :disable="this.currency !== null"
            />
          </div>
          <div
            class="col-xs-12 col-sm-12 col-md-12 q-px-xs q-pb-md"
            v-if="bankCurrency.value !== 'MYR' && bankCurrency.value && !isProcessing"
          >
            <q-input
              outlined
              stack-label
              class="q-pb-none"
              v-model="bankSwiftCode"
              label="Bank Swift Code"
              lazy-rules
              :rules="[ val => val !== '' && val !== null ]"
            />
          </div>

          <div
            class="col-xs-12 col-sm-12 col-md-12 q-px-xs q-pb-md"
            v-if="accountsList.length !== 0"
          >
            <q-select
              outlined
              stack-label
              v-model="taggedAccounts"
              :options="accountsList"
              label="Associate with other active accounts"
              multiple
              emit-value
              map-options
              use-chips
              :loading="isLoadingAssociatesAccount"
            >
              <template v-slot:option="scope">
                <q-item v-bind="scope.itemProps" v-on="scope.itemEvents">
                  <q-item-section>
                    <q-item-label v-html="scope.opt.label"></q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-toggle v-model="taggedAccounts" :val="scope.opt.value" :disable="scope.opt.disable" />
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <small>* Please note, if third party account(s) is/are associated, bank account registration will be rejected.</small>
          </div>

          <div class="col-xs-12 col-sm-12 col-md-12 q-px-xs q-pb-md" v-if="this.is3rdPartyAccount">
            <azure-uploader
              ref="AzureUploader"
              label="Attach Bank Account Statement"
              account="nadiablob"
              sas="?sv=2018-03-28&ss=bfqt&srt=sco&sp=rwdlacup&se=2023-05-07T08:43:39Z&st=2019-05-07T00:43:39Z&spr=https&sig=IkvEUVOmAdLCOyJpUKeQBjpWo7cw7hqVll%2FZQeokC0Y%3D"
              :containerName="bankAttachmentContainer"
              :path="customerId"
              accept=".jpg, .jpeg, .png, .pdf, .docx"
            />
          </div>

        </div>

        <div class="row q-pb-md" v-if="isJointAccount">
          <div class="col col-xs-12 col-sm-12 col-md-12 q-px-xs">
            <q-input
              outlined
              stack-label
              class="q-pb-none"
              autogrow
              v-model="jointHolderName"
              label=" Joint Account Holder Name"
              lazy-rules
              :rules="[ val => val !== '' && val !== null ]"
            />
            <small>If more than one joint holder, separate with ';'</small>
          </div>
        </div>

        <div v-if="is3rdPartyAccount">
          <q-separator spaced />

          <div class="text-bold text-center q-py-md">3rd Party Individual</div>

          <div class="row q-pb-md">
            <div class="col col-xs-12 col-sm-12 col-md-12">
              <q-table
                flat
                dense
                bordered
                table-header-class="bg-primary text-white"
                :data="thirdPartyDetailsTableData"
                :columns="thirdPartyTableColumns"
                row-key="index"
                hide-bottom
              >
                <template v-slot:body-cell-relationship="props">
                  <q-td align="right">
                    {{ props.row.relationship !== 'Other' ? props.row.relationship : 'Others - ' + props.row.relationshipOther }}
                  </q-td>
                </template>

                <template v-slot:body-cell-action="props">
                  <q-td auto-width>
                    <q-btn
                      flat
                      icon="fas fa-times-circle"
                      size="sm"
                      dense
                      @click="removeThirdParty(props.row.index - 1)"
                      color="negative"
                    />
                  </q-td>
                </template>
              </q-table>
            </div>

            <div class="col col-xs-12 col-sm-12 col-md-12 text-center q-py-md">
              <q-btn
                icon="fas fa-plus-circle"
                size="sm"
                class="text-black"
                outline
                v-model="showAddNew3rdParty"
                color="primary"
                label="Add New"
                @click="showAddNew3rdParty = true"
                :disable="thirdPartyDetails.length >= 2"
              />
            </div>
          </div>

          <q-dialog v-model="showAddNew3rdParty" persistent>
            <div style="width: 500px; max-width: 80vw;">
              <add-third-party
                :customerId="customerId"
                :env="env"
                :apiUrl="apiUrl"
                :onboardingAPIToken="onboardingAPIToken"
                @submit="addNewThirdParty"
                @cancel="showAddNew3rdParty = false"
              />
            </div>
          </q-dialog>
        </div>

        <div v-if="bankCurrency.value !== 'MYR' && bankCurrency.value && !isProcessing">
          <q-separator spaced />

          <div class="text-bold text-center q-py-md">Intermediary Bank</div>

          <div class="row q-pb-md">
            <div class="col-xs-12 col-sm-12 col-md-12 q-px-xs q-pb-md">
              <q-input
                outlined
                stack-label
                class="q-pb-none"
                color="primary"
                label="Intermediary Bank Name"
                v-model="intermediaryBankName"
              />
            </div>
            <div class="col-xs-12 col-sm-12 col-md-12 q-px-xs q-pb-md">
              <q-input
                outlined
                stack-label
                class="q-pb-none"
                color="primary"
                label="Intermediary Bank Branch"
                v-model="intermediaryBankBranch"
              />
            </div>
            <div class="col-xs-12 col-sm-6 col-md-6 q-px-xs q-pb-md">
              <q-input
                outlined
                stack-label
                class="q-pb-none"
                color="primary"
                label="Intermediary Bank Swift Code"
                v-model="intermediaryBankSwiftCode"
              />
            </div>
            <div class="col-xs-12 col-sm-6 col-md-6 q-px-xs q-pb-md">
              <q-input
                outlined
                stack-label
                class="q-pb-none"
                color="primary"
                label="Intermediary Bank Account No."
                v-model="intermediaryBankAccountNo"
              />
            </div>
          </div>
        </div>
      </q-card-section>

      <q-separator />
      <q-linear-progress query v-if="isProcessing" />

      <q-card-actions align="center">
        <q-btn label="Cancel" color="negative" @click="cancel" />
        <q-btn label="Submit" color="primary" type="submit" :disable="isProcessing" />
      </q-card-actions>
    </q-form>

    <q-dialog v-model="promptTac" persistent>
      <tac
        :customerId="customerId"
        purpose="Bank Account Registration"
        :validityInMinutes="3"
        :env="env"
        :apiUrl="apiUrl"
        :notificationAPIToken="notificationAPIToken"
        :cifAPIToken="cifAPIToken"
        @submit="tacSubmit"
        @cancel="tacCancel"
      />
    </q-dialog>
  </q-card>
</template>

<script>
import axios from "axios";
import AzureUploader from "../azure-uploader/AzureUploader.vue";
import Tac from "../../tac/Tac.vue";
import AddThirdParty from "../add-new-bank/AddThirdParty.vue";
import { QInput, QSelect, QLinearProgress, QCard, QCardSection } from "quasar";

export default {
  name: "AddNewBankAccount",

  components: {
    QInput,
    QSelect,
    QCard,
    QCardSection,
    QLinearProgress,
    AzureUploader,
    Tac,
    AddThirdParty
  },

  props: {
    customerId: { type: String, required: true },
    accountId: { type: String, required: true },
    userId: { type: String, required: false },
    channel: { type: String, required: false },
    currency: { type: String, required: false, default: null },
    default: { type: Boolean, required: false },
    isSelf: { type: Boolean, required: false, default: false },

    env: { type: String, required: false, default: 'development' },
    apiUrl: { type: String, required: false },
    cifAPIToken: { type: String, required: true },
    notificationAPIToken: { type: String, required: true },
    onboardingAPIToken: { type: String, required: true },

    agentEmail: { type: String, required: true }
  },

  data() {
    return {
      holderName: "",
      bank: '',
      bankAccNumber: "",
      bankCode: "",
      bankSwiftCode: "",
      bankList: [],
      bankCurrencyList: [],
      bankCurrency: "",
      isDefaultBankAcc: false,
      isJointAccount: false,
      jointHolderName: "",
      idTypeList: [],
      validationId: "",
      validationIdType: '',
      intermediaryBankName: "",
      intermediaryBankBranch: "",
      intermediaryBankSwiftCode: "",
      intermediaryBankAccountNo: "",
      bankAccountAttachment: "",
      BankAccountStatementFileData: "",
      BankAccountStatementFileName: "",
      BankAccountStatementFileURL: "",
      isFilesAttached: false,
      files: "",
      attachmentPath: "",
      isProcessing: false,
      customerAccounts: [],
      taggedAccounts: [],
      customerInfo: [],
      promptTac: false,
      is3rdPartyAccount: false,
      showAddNew3rdParty: false,
      thirdPartyDetails: [],
      thirdPartyTableColumns: [
        { name: 'index', label: '#', field: 'index' },
        { name: 'name', label: 'Name', field: 'name' },
        { name: 'id', label: 'IC/Passport', field: 'id' },
        { name: 'relationship', label: 'Relationship', field: 'relationship' },
        { name: 'purpose', label: 'Purpose Of Transfer', field: 'purpose' },
        { name: 'action', label: '', field: 'action' },
      ],
      isLoadingAssociatesAccount: false
    };
  },

  methods: {
    async submit() {
      if (this.is3rdPartyAccount && !this.$refs.AzureUploader.isFilesAttached) {
        return alert('No bank account statement attached.')
      }

      if (this.is3rdPartyAccount && this.thirdPartyDetails.length === 0) {
        return alert('No third party account details submitted.')
      }

      if (this.isSelf) {
        return this.$set(this, 'promptTac', true)
      }

      this.toggleProgressBar(true)
      this.registerbankAccount()
    },

    tacSubmit(status) {
      if (status.isVerified) {
        this.$set(this, "promptTac", false);
        return this.registerbankAccount();
      }

      this.$set(this, "promptTac", false);
      this.toggleProgressBar(false);
      return alert("Oops it seems that your TAC verification failed. Please try again.");
    },

    tacCancel() {
      this.$set(this, "promptTac", false);
      this.toggleProgressBar(false);
    },

    addNewThirdParty(payload) {
      this.thirdPartyDetails.push(payload);
      this.showAddNew3rdParty = false;
    },

    removeThirdParty(index) {
      this.thirdPartyDetails.splice(index, 1);
    },

    async registerbankAccount() {
      if (this.is3rdPartyAccount) {
        await this.$refs.AzureUploader.upload()
        .then(resp => {
          this.BankAccountStatementFileData = '';
          this.BankAccountStatementFileName = resp.azure.filename;
          this.BankAccountStatementFileURL = resp.azure.blobName;
        })
        .catch(err => {
          console.log(err);
          this.toggleProgressBar(false);
          return alert("Oops, something went wrong. Please try again shortly.");
        });
      }

      this.submitApplication()
        .then(resp => {

          if (this.isSelf) {
            axios({ 
              method: "POST",
              url: this.apiUrl + '/cif/account/bankaccount/registration/clientapproval',
              headers: { Authorization: "Bearer " + this.cifAPIToken },
              data: { 
                  "ApplicationId": resp.data.ApplicationId,
                  "Status": 'Approve',
                  "RejectRemarks": ''
              }
            })
            .then(resp => { 
              this.emitToParent();
              this.toggleProgressBar(false);
            })
            .catch(err => {
              console.log(err);
              this.toggleProgressBar(false);
              return alert(
                "Oops, something went wrong. Please try again shortly."
              );
            });
          }

          else {
            this.sendEmailNotification(resp.data.ApplicationId, resp.data.ApplicationFormId)
            .then(resp => {
              this.emitToParent();
              this.toggleProgressBar(false);
            })
            .catch(err => {
              console.log(err);
              this.toggleProgressBar(false);
              return alert(
                "Oops, something went wrong. Please try again shortly."
              );
            });
          }

        })
        .catch(err => {
          this.toggleProgressBar(false);
          alert("Oops, something went wrong. Please try again shortly.");
          console.log(err);
        });
    },

    submitApplication() {

      // Remove un-needed key
      this.thirdPartyDetails.forEach(
        obj => delete obj.RelationshipWithHolderLabel
      );

      return axios({
        method: "POST",
        url: this.apiUrl + "/cif/account/bankaccount/registration",
        headers: { Authorization: "Bearer " + this.cifAPIToken },
        data: {
          CustomerId: this.customerId,
          AccountId: this.taggedAccounts,
          Channel: this.channel ? this.channel : '',
          UserId: this.userId ? this.userId : '',
          BankAccName: this.holderName,
          BankAccNumber: this.bankAccNumber,
          BankName: this.bank.value.micr,
          Currency: this.bankCurrency.value,
          isDefault: this.isDefaultBankAcc,
          isJointAccount: this.isJointAccount,
          JointHolderName: this.jointHolderName,
          SwiftCode: this.bankSwiftCode,
          IntermediaryBank: this.intermediaryBankName,
          IntermediaryBankBranch: this.intermediaryBankBranch,
          IntermediaryBankSwiftCode: this.intermediaryBankSwiftCode,
          IntermediaryBankAccountNumber: this.intermediaryBankAccountNo,
          ValidationIdType: this.validationIdType.value,
          ValidationId: this.validationId,
          BankAccountStatementFileData: this.BankAccountStatementFileData,
          BankAccountStatementFileName: this.BankAccountStatementFileName,
          BankAccountStatementFileURL: this.BankAccountStatementFileURL,
          isThirdPartyBankAccount: this.is3rdPartyAccount,
          ThirdPartyBankAccount: this.is3rdPartyAccount ? this.thirdPartyDetails : ""
        }
      });
    },

    sendEmailNotification(appId, appFormId) {
      
      const hostname = this.env === 'production' ? 'https://transaction.nadia.com.my' : 'https://transaction-staging.nadia.com.my';

      return axios({
        method: "POST",
        url: this.apiUrl + "/notification",
        headers: { Authorization: "Bearer " + this.notificationAPIToken },
        data: {
          CustomerID: this.customerId,
          Application: 'Bank Account',
          NotificationType: "emailtemplate",
          ContentTemplate: {
            AppID: "BANKACCOUNT",
            Code: "PENDING_CUST_VERIFICATION"
          },
          ContentVariables: [
            {
              Keyword: "<v_ApplicationFormId>",
              Value: appFormId
            },
            {
              Keyword: "<v_FullName>",
              Value: this.customerInfo.Name
            },
            {
              Keyword: "<v_URL>",
              Value: hostname + "/ClientLogin?ApplicationId=" + appId + "&Type=BankAccount"
            }
          ],
          email: {
            serviceId: "1",
            toAddress: this.customerInfo.Email,
            ccAddress: this.agentEmail,
            bccAddress: "",
            subject: "",
            body: "",
            attachments: []
          }
        }
      })
    },

    cancel() {
      this.toggleProgressBar(false);
      this.$emit("cancel", "Add new bank cancelled.");
    },

    toggleProgressBar(payload) {
      this.$set(this, "isProcessing", payload);
    },

    // @TODO: Remove this after UAT confirmed
    keyIsNumber(e) {
      e = (e) ? e : window.event;
      let charCode = (e.which) ? e.which : e.keyCode;

      return (charCode > 31 && (charCode < 48 || charCode > 57) && (charCode < 95 || charCode > 105)) ? e.preventDefault() : true
    },

    inputNoSpecialChar(e) {
      let regex = new RegExp("^[a-zA-Z0-9]*$")
      return !regex.test(e.key) ? e.preventDefault() : true
    },

    checkSelectedBank() {
      if (this.bank === '') {
        alert('Please select your bank from the bank list selection.')
        this.$refs.bankList.focus()
      }
    },

    emitToParent() {
      this.$emit("submit", {
        label: this.bank.label + " - " + this.bankAccNumber,
        value: {
          bankName: this.bank.label,
          accHolderName: this.holderName,
          accNo: this.bankAccNumber,
          micrCode: this.bank.value.micr
        }
      });
    },

    fetchCustomerAccounts() {

      this.$set(this, "isLoadingAssociatesAccount", true);

      // CIF API : Fetch customer accounts
      axios({
        method: "GET",
        url: this.apiUrl + "/cif/account/bankaccount/associateaccount",
        params: {
          pcustomerid: this.customerId,
          pisjointbankaccount: this.isJointAccount ? 1 : 0,
          pisthirdpartybankaccount: this.is3rdPartyAccount ?  1 : 0,
          pcurrency: this.bankCurrency.value
        },
        headers: {
          Authorization: "Bearer " + this.cifAPIToken
        }
      })
        .then(resp => {
          this.$set(this, "customerAccounts", resp.data.GetBankAccountAssociateAccountResponse.GetBankAccountAssociateAccountResult);
          this.$set(this, "isLoadingAssociatesAccount", false);

          this.$set(this, "taggedAccounts", 
            this.customerAccounts
              .filter(v => v.AccountId === this.accountId)
              .map(v => v.AccountId)
            )
        })
    }

  },

  computed: {
    accountsList() {
      let accounts = this.customerAccounts.map(v => {
        return {
          label: v.AccountType + " (" + v.AccountId + ")",
          value: v.AccountId,
          disable: v.AccountId === this.accountId ? true : false
        }
      })

      return accounts
    },

    validationIdMask() {
      if (this.validationIdType.value === "NewIC") {
        return "######-##-####";
      }
      return "";
    },

    thirdPartyDetailsTableData() {
      return this.thirdPartyDetails.map((v, index) => {
        return {
          index: index + 1,
          name: v.Name,
          id: v.NRICorOther,
          mobileNo: v.TelMobile,
          relationship: v.RelationshipWithHolderLabel,
          relationshipOther: v.RelationshipWithHolderOth,
          purpose: v.Purpose
        };
      });
    },

    isLengthAllowed() {
      if (this.bank !== '' && this.bank.value.AllowedLength) {
        let valLength = this.bankAccNumber.toString().length
        let requiredLengths = this.bank.value.AllowedLength.map(v => v.length)

        return requiredLengths.includes(valLength)
      }

      return true
    },

    bankAttachmentContainer() {
      return this.env === 'development' ? 'bankaccount-uat' : 'bankaccount'
    }

  },

  async mounted() {
    
    this.toggleProgressBar(true);

    // Assign default if default pros is available
    this.$set(this, 'isDefaultBankAcc', this.default ? true : false)

    // Onboarding API: Fetch form data
    await axios({
      method: "GET",
      url: this.apiUrl + "/onboarding/form/lookups",
      headers: {
        Authorization: "Bearer " + this.onboardingAPIToken
      }
    })
      .then(resp => {
        this.idTypeList = resp.data.BankAccountValidationType.map(item => {
          return { label: item.Name, value: item.Value };
        });

        this.bankList = resp.data.Bank.map(item => {
          return { 
            label: item.Name, 
            value: {
              micr: item.Value,
              AllowedLength: item.BankAccountNoLength ? item.BankAccountNoLength : null
            }
          };
        });

        this.bankCurrencyList = resp.data.Currency.map(item => {
          return { 
            label: item.CurrencyName,
            value: item.CurrencyCode
          };
        });

        if (this.currency !== null) {
          this.bankCurrency = this.bankCurrencyList
            .filter(item => item.value === this.currency)
            .reduce((init, item) => item, {})
        }
      })
      .catch(err => console.log(err));

    // CIF API : Fetch customer information
    axios({
      method: "GET",
      url: this.apiUrl + "/cif/" + this.customerId + "/enquiry/customerinformation",
      headers: {
        Authorization: "Bearer " + this.cifAPIToken
      }
    })
      .then(resp => {
        this.$set(this, "customerInfo", resp.data.CIF.CustomerDetail);
        this.$set(this, "holderName", this.customerInfo.Name);
        
        this.$set(
          this,
          "validationIdType",
          this.customerInfo.Nationality === "MY"
            ? this.idTypeList.filter(v => v.label === 'NRIC').reduce((init, v) => v, {})
            : ""
        );

        this.$set(
          this,
          "validationId",
          this.customerInfo.NewID !== ""
            ? this.customerInfo.NewID
            : this.customerInfo.OldID
        );

        // @TODO: Remove after UAT
        // this.$set(
        //   this,
        //   "customerAccounts",
        //   resp.data.CIF.Accounts.filter(v => {
        //     return v.AccountStatus === "A" && v.AccountID !== this.accountId;
        //   })
        // );

        this.toggleProgressBar(false);
      })
      .catch(err => console.log(err));

    // Fetch customer accounts
    this.fetchCustomerAccounts()

  }
};
</script>
